import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { CircularProgress } from '@mui/material';

import { useVolumetricStats } from 'src/use-cases/performance/usePerformance';
import { CardStat } from './Stat/CardStat';
import { LoaderVolumetric } from './LoaderVolumetric';
import { SectionVolumetric } from './SectionDetail/SectionVolumetric';
import { getProviderModalityCombinations, getVolumetricStatsList } from './helpers';

import { IProviderModalityProps } from './types';

interface PerformanceContentVolumetricProps {
  dataFilter: Record<string, any>;
}

export const PerformanceContentVolumetric: FC<PerformanceContentVolumetricProps> = ({ dataFilter }) => {
  const { watch } = useFormContext();
  const formValues = watch();
  const [visibleCount, setVisibleCount] = useState(2);
  const [loadMoreElement, setLoadMoreElement] = useState<HTMLDivElement | null>(null);

  const { data: dataStats, isLoading: isLoadingStats } = useVolumetricStats({
    filterConfigs: formValues,
  });

  const { currencyReference } = formValues;

  const statsList = useMemo(() => getVolumetricStatsList(dataStats, { unitCurrency: currencyReference }), [dataStats, currencyReference]);

  // Get combinations for this component
  const combinations = useMemo(() => getProviderModalityCombinations(formValues, dataFilter), [formValues, dataFilter]);

  // Reset visible count when combinations change
  useEffect(() => {
    setVisibleCount(2);
  }, [combinations.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreElement) {
      return;
    }

    const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
      const [entry] = entries;
      if (entry.isIntersecting && visibleCount < combinations.length) {
        setVisibleCount((prev) => Math.min(prev + 2, combinations.length));
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: '20px',
    });

    observer.observe(loadMoreElement);

    // eslint-disable-next-line consistent-return
    return function cleanup() {
      observer.disconnect();
    };
  }, [loadMoreElement, visibleCount, combinations.length]);

  return (
    <div>
      {isLoadingStats ? (
        <LoaderVolumetric />
      ) : (
        <>
          <div className='pt-8 px-32 pb-24 grid gap-24 border-b border-neutral-20 sm:grid-cols-1 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5'>
            {statsList.map((item) => (
              <CardStat key={`card-stat-main-${item?.type}`} type={item?.type} values={item?.values} />
            ))}
          </div>

          <div className='border-t border-white px-16 xl:px-32 py-20 xl:py-24 flex flex-col gap-32 xl:gap-40'>
            {combinations.slice(0, visibleCount).map((item: IProviderModalityProps) => (
              <SectionVolumetric key={`performance-${item.provider}-${item.modality}`} provider={item.provider} modality={item.modality} isActive />
            ))}
            {visibleCount < combinations.length && (
              <div ref={setLoadMoreElement} className='text-center py-12'>
                <CircularProgress size={24} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
